import React from "react";
import { createPortal } from "react-dom";
import { Icons } from "../Icons";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  headerGradient?: "purple" | "blue" | "green" | "gray";
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  children,
  size = "md",
  headerGradient = "gray",
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
  };

  const responsiveSizeClasses = {
    sm: "mx-4 sm:mx-auto",
    md: "mx-4 sm:mx-auto",
    lg: "mx-4 sm:mx-auto",
    xl: "mx-4 sm:mx-auto",
  };

  const gradientClasses = {
    purple:
      "from-purple-50 to-purple-100 dark:from-purple-950 dark:to-slate-800",
    blue: "from-blue-50 to-blue-100 dark:from-blue-950 dark:to-slate-800",
    green: "from-green-50 to-green-100 dark:from-green-950 dark:to-slate-800",
    gray: "from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800",
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 transition-opacity backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div className="min-h-full flex items-center justify-center py-2 sm:py-4 px-2 sm:px-4">
        <div
          className={`bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-600 w-full ${sizeClasses[size]} ${responsiveSizeClasses[size]} max-h-[95vh] sm:max-h-[90vh] flex flex-col relative overflow-hidden`}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`bg-gradient-to-r ${gradientClasses[headerGradient]} px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl border-b border-gray-200 dark:border-slate-600 flex items-center justify-between gap-2 flex-shrink-0`}
          >
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              {icon && (
                <span className="flex items-center flex-shrink-0 text-gray-700 dark:text-slate-200 [&>.MuiSvgIcon-root]:!text-xl">
                  {icon}
                </span>
              )}
              <span className="truncate">{title}</span>
            </h3>
            <button
              onClick={onClose}
              type="button"
              className="text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors duration-200 flex-shrink-0"
              aria-label="Fechar"
            >
              <Icons.Close sx={{ fontSize: 22 }} />
            </button>
          </div>

          <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0 relative bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
